// Snel vastleggen (spec T1): one plus button on every screen; Enter puts it in the inbox and the field stays open.
// With AI (AI golf A, B33): typed or spoken with the keyboard's microphone, the app puts it in its place, and a photo
// or screenshot becomes appointments and tasks.
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Camera, Mic, Plus } from "lucide-react";
import { toast } from "sonner";
import { ApiError } from "../lib/api";
import { aiImage, shrinkImage, useAiEnabled } from "../lib/ai";
import { captureTask } from "../lib/tasks";
import { navigate, usePath } from "../lib/hooks";
import { Sheet } from "./Sheet";
import { Button } from "./ui";

export function CaptureButton() {
  // The app icon's shortcut "Vastleggen" (golf 9) opens straight into this window, over Vandaag.
  const path = usePath();
  const [open, setOpen] = useState(path === "/vastleggen");
  const [shortcutPath, setShortcutPath] = useState(path);
  if (path !== shortcutPath) {
    setShortcutPath(path);
    if (path === "/vastleggen") setOpen(true);
  }
  useEffect(() => {
    if (path === "/vastleggen") navigate("/", { replace: true });
  }, [path]);
  const [title, setTitle] = useState("");
  const [added, setAdded] = useState(0);
  const [reading, setReading] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const photo = useRef<HTMLInputElement>(null);
  const ai = useAiEnabled();

  async function submit(event: FormEvent) {
    event.preventDefault();
    const text = title.trim();
    if (!text) return;
    // Clear first: the next one may be typed while this one is being saved.
    setTitle("");
    setAdded((n) => n + 1);
    input.current?.focus();
    await captureTask(text);
  }

  async function readPhoto(file: File | undefined) {
    if (!file) return;
    setReading(true);
    try {
      const image = await shrinkImage(file);
      const { placed, review } = await aiImage(image.mediaType, image.data);
      const parts = [placed ? `${placed} op de goede plek gezet` : null, review ? `${review} om te controleren bij Taken` : null].filter(
        Boolean,
      );
      toast(parts.length ? `Uit de foto: ${parts.join(", ")}` : "Ik vond niets om te onthouden op deze foto.");
    } catch (error) {
      toast(
        error instanceof ApiError && error.status > 0 && error.status < 600 ? error.message : "De foto lezen kan alleen met verbinding.",
      );
    } finally {
      setReading(false);
      if (photo.current) photo.current.value = "";
    }
  }

  function change(next: boolean) {
    setOpen(next);
    if (!next && added) toast(added === 1 ? "1 ding vastgelegd" : `${added} dingen vastgelegd`);
    if (!next) setAdded(0);
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Snel vastleggen"
        className="fixed right-4 bottom-[calc(5rem+env(safe-area-inset-bottom))] z-20 flex size-16 items-center justify-center rounded-full bg-pine text-paper shadow-lg"
      >
        <Plus aria-hidden className="size-8" strokeWidth={2.4} />
      </button>
      <Sheet
        open={open}
        onOpenChange={change}
        title="Snel vastleggen"
        description={
          ai
            ? "De app zet het op de goede plek. Twijfelt hij, dan vraagt hij het je bij Taken."
            : "Komt bij Nieuw in Taken. Later plan je het in."
        }
      >
        <form onSubmit={submit} className="flex flex-col gap-3">
          <label htmlFor="capture" className="sr-only">
            Wat wil je kwijt?
          </label>
          <input
            id="capture"
            ref={input}
            autoFocus
            enterKeyHint="done"
            autoComplete="off"
            value={title}
            maxLength={500}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={ai ? "Bijvoorbeeld: tandarts dinsdag om 10 uur" : "Bijvoorbeeld: fietsband plakken"}
            className="min-h-14 rounded-xl border-2 border-mist bg-paper px-3 text-lg text-ink placeholder:text-ink-soft"
          />
          {/* Spraak via het toetsenbord (B33): geen eigen spraakdienst. */}
          <p className="flex items-center gap-2 text-sm text-ink-soft">
            <Mic aria-hidden className="size-4 shrink-0 text-pine" />
            Liever praten? Tik op de microfoon van je toetsenbord en zeg het gewoon.
          </p>
          <Button type="submit" disabled={!title.trim()}>
            {ai ? "Vastleggen" : "Bij Nieuw zetten"}
          </Button>
          {ai && (
            <>
              <input
                ref={photo}
                type="file"
                accept="image/*"
                className="sr-only"
                aria-label="Foto of schermafbeelding kiezen"
                onChange={(e) => void readPhoto(e.target.files?.[0])}
                tabIndex={-1}
              />
              <Button type="button" variant="secondary" disabled={reading} onClick={() => photo.current?.click()}>
                <Camera aria-hidden className="size-5" />
                {reading ? "Foto lezen" : "Foto of schermafbeelding"}
              </Button>
            </>
          )}
          {added > 0 && (
            <p role="status" className="text-center text-ink-soft">
              {added === 1 ? "1 ding vastgelegd. Nog iets?" : `${added} dingen vastgelegd. Nog iets?`}
            </p>
          )}
        </form>
      </Sheet>
    </>
  );
}
